import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, inject } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { fhirR4 } from '@smile-cdr/fhirts';
import { Store } from '@ngrx/store';
import { asapScheduler } from 'rxjs';
import { ResourceType } from '../models/ResourceType';
import { HomeNavState, UnsupportedTypeDetail } from '../models/HomeNavState';
import {
  setPersistentError,
  setLogError,
  setInitialValues,
  nextLandingStage,
} from '../state/form.actions';
import {
  selectLandingStages,
  selectPersistentError,
} from '../state/form.selectors';
import { MessageBoxState } from '../models/MessageBoxState';
import { LaunchConfigError } from '../models/LaunchConfigError';
import { LAUNCH_FAILED, PersistentError } from '../models/PersistentError';
import { AsyncPipe } from '@angular/common';
import { LaunchProgressComponent } from './launch-progress/launch-progress.component';
import { FooterComponent } from '../shared/footer/footer.component';
import { LaunchContextService } from './launch-context.service';
import {
  LaunchHooks,
  QuestionnaireLaunchService,
} from './questionnaire-launch.service';

@Component({
  templateUrl: './landing.component.html',
  imports: [LaunchProgressComponent, FooterComponent, AsyncPipe, TranslatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LandingComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly launchContext = inject(LaunchContextService);
  private readonly questionnaireLaunch = inject(QuestionnaireLaunchService);

  questionnaire;
  persistentError$ = this.store.select(selectPersistentError);
  stages$ = this.store.select(selectLandingStages);
  unsupportedTypeDetail: UnsupportedTypeDetail | undefined;
  navigateState: HomeNavState | undefined;

  readonly hooks: LaunchHooks = {
    advance: () => this.nextStage(),
    setQuestionnaire: (questionnaire) => (this.questionnaire = questionnaire),
    getUnsupportedDetail: () => this.unsupportedTypeDetail,
  };

  ngOnInit() {
    this.checkForBlockersOrProceed();
  }

  async checkForBlockersOrProceed() {
    if (this.launchContext.checkConfigError()) {
      asapScheduler.schedule(() =>
        this.showPersistentError('Error.Persistent.Reason.Configuration'),
      );
      return;
    }
    try {
      const appContext = await this.launchContext.initAndCheckContext(
        this.hooks.advance,
      );
      const decision = this.launchContext.classifyMarshalContext(appContext);
      if (decision.kind === 'relaunch') {
        this.proceed(
          await this.questionnaireLaunch.relaunch(
            decision.qr,
            appContext,
            this.hooks,
            decision.task,
          ),
        );
      } else if (decision.kind === 'cdex') {
        this.proceed(
          await this.questionnaireLaunch.cdexLaunch(
            decision.task,
            appContext,
            this.hooks,
          ),
        );
      } else {
        const newLaunch = await this.launchContext.resolveNewLaunch(appContext);
        if (newLaunch.kind === 'messageBox') {
          this.navigateToMessageBox(newLaunch.state);
          return;
        }
        if (newLaunch.kind === 'persistentError') {
          this.dispatchPersistentError(newLaunch.error);
          return;
        }
        this.proceed(
          await this.questionnaireLaunch.launchWithQuestionnaire(
            appContext,
            newLaunch.canonicalUrl,
            newLaunch.coverage,
            newLaunch.order,
            this.hooks,
          ),
        );
      }
    } catch (err) {
      if (err instanceof LaunchConfigError) {
        this.showPersistentError(err.reason);
        return;
      }
      if (this.launchContext.handleLaunchError(err)) {
        this.showPersistentError();
      }
    }
  }

  proceed(navigateState: HomeNavState) {
    if (navigateState.populateError) {
      this.launchContext.logError(navigateState.populateError);
    }
    this.navigateState = navigateState;
    this.questionnaire = navigateState.questionnaire;
    this.checkForValidLaunch(navigateState);
  }

  showPersistentError(reason?: string) {
    this.dispatchPersistentError({ ...LAUNCH_FAILED, reason });
  }

  dispatchPersistentError(persistentError: PersistentError) {
    this.store.dispatch(setPersistentError({ persistentError }));
  }

  nextStage() {
    this.store.dispatch(nextLandingStage());
  }

  navigate(state: HomeNavState) {
    this.router.navigateByUrl('home', { skipLocationChange: true });
    asapScheduler.schedule(() => this.store.dispatch(setInitialValues(state)));
    this.nextStage();
  }

  navigateToMessageBox(state: MessageBoxState) {
    const navigationExtras: NavigationExtras = {
      state: { ...state },
      skipLocationChange: true,
    };
    this.router.navigateByUrl('message-box', navigationExtras);
  }

  handleUnsupportedItems(event) {
    this.unsupportedTypeDetail = event.detail;
    this.checkIfReadyToNavigate();
  }

  checkForValidLaunch(navigateState) {
    if (this.questionnaireLaunch.isValidPerformerType(navigateState.questionnaire)) {
      this.checkIfReadyToNavigate();
    } else {
      this.navigateToMessageBox(MessageBoxState.QuestionnaireAccessRestricted);
    }
  }

  checkIfReadyToNavigate() {
    if (this.unsupportedTypeDetail && this.navigateState) {
      if (this.unsupportedTypeDetail.required?.length > 0) {
        this.showPersistentError('Error.Persistent.Reason.CannotRender');
      } else {
        asapScheduler.schedule(() => {
          this.store.dispatch(
            setLogError({
              optional: this.createUnsupportedOperationOutcome(
                this.unsupportedTypeDetail?.optional,
                'not-supported',
              ),
              invalid: this.createUnsupportedOperationOutcome(
                this.unsupportedTypeDetail?.unsupportedControl,
                'invalid',
              ),
              questionnaireErrors: this.navigateState?.questionnaireResponse[
                'contained'
              ]?.find(
                (containedResource) =>
                  containedResource.resourceType ===
                  ResourceType.OperationOutcome,
              ),
            }),
          );
        });
        this.navigate(this.navigateState);
      }
    } else if (
      !this.unsupportedTypeDetail &&
      this.navigateState?.questionnaire &&
      !this.navigateState.questionnaire.item
    ) {
      this.showPersistentError('Error.Persistent.Reason.NoItems');
    }
  }

  createUnsupportedOperationOutcome(
    optionalItems,
    type,
  ): fhirR4.OperationOutcome | undefined {
    if (optionalItems) {
      const retVal = new fhirR4.OperationOutcome();
      retVal.resourceType = ResourceType.OperationOutcome;
      retVal.issue = optionalItems.map((item) => {
        return {
          severity: 'error',
          code: type,
          diagnostics: item.text ?? item.linkId,
          expression: [item.linkId],
        };
      });
      return retVal;
    }
    return undefined;
  }
}
